// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@rpsc/App/Components/Common/XngZone";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import { StyledHr } from "@rpsc/XDL/Utilities";
import React, { FC } from "react";

interface XTitledSectionStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    container?: InjectableCss;
    link?: LinkPresentationProps;
    leftSide?: InjectableCss;
    content?: InjectableCss;
    styledHr?: InjectableCss;
}

export const xTitledSectionStyles: XTitledSectionStyles = {};

const styles = xTitledSectionStyles;

const enum sectionFields {
    title = "title",
    variant = "variant",
    buttonTitle = "buttonTitle",
    buttonLink = "buttonLink",
    showBorder = "showBorder",
    margin = "margin",
    addZone = "addZone",
}

interface OwnProps extends WidgetProps {
    fields: {
        [sectionFields.title]: string;
        [sectionFields.variant]: keyof ThemeTypography;
        [sectionFields.buttonTitle]: string;
        [sectionFields.buttonLink]: LinkFieldValue | string;
        [sectionFields.showBorder]: boolean;
        [sectionFields.margin]: string;
        [sectionFields.addZone]: boolean;
    };
}
type Props = OwnProps;

const XTitledSection: FC<Props> = ({
    fields: { title, variant, buttonTitle, buttonLink, showBorder, margin, addZone },
    id,
}) => {
    const _buttonLink = typeof buttonLink === "string" ? buttonLink : useGetLink(buttonLink).url;

    return (
        <StyledWrapper {...styles.wrapper} {...{ margin }}>
            <StyledWrapper {...styles.container} {...{ showBorder }}>
                {title && (
                    <StyledWrapper {...styles.leftSide}>
                        <Typography variant={variant} as="h1" {...styles.title}>
                            {title}
                        </Typography>
                    </StyledWrapper>
                )}
                {!!buttonTitle && !!_buttonLink && (
                    <Link {...styles.link} href={_buttonLink} aria-label="View More">
                        {buttonTitle}
                    </Link>
                )}
            </StyledWrapper>
            {addZone && (
                <StyledWrapper {...styles.content}>
                    <XngZone zoneName="content" contentId={id} hint="Content" />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
const xTitledSectionWidget: WidgetModule = {
    component: XTitledSection,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                editorTemplate: "TextField",
                name: sectionFields.title,
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: sectionFields.variant,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "H1",
                        value: "h1",
                    },
                    {
                        displayName: "H2",
                        value: "h2",
                    },
                    {
                        displayName: "H3",
                        value: "h3",
                    },
                ],
                defaultValue: "h1",
                fieldType: "General",
            },
            {
                editorTemplate: "TextField",
                name: sectionFields.buttonTitle,
                defaultValue: "",
                fieldType: "General",
            },

            {
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Page",
                    value: "",
                },
                name: sectionFields.buttonLink,
                fieldType: "General",
            },
            {
                name: sectionFields.showBorder,
                displayName: "Show border above content",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                editorTemplate: "TextField",
                name: sectionFields.margin,
                defaultValue: "0",
                fieldType: "General",
            },
            {
                name: sectionFields.addZone,
                displayName: "Add Zone",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};
export default xTitledSectionWidget;
